import { supabase } from '@/integrations/supabase/client';

/**
 * Ending a customer visit WITHOUT completing it.
 *
 * Cancellation and deletion are not timer commands. A manager deleting a
 * ticket has not asserted "Tech A stopped working now" or "Tech B resumed from
 * pause" — but until 8G.2 the product made both assertions on their behalf.
 * Measured on real rows: deleting a ticket with two active technicians closed
 * BOTH their measured sessions, and cancelling one with two paused technicians
 * left both pauses dangling on a terminal ticket with no episode to return to.
 *
 * The server now refuses while any technician is working, paused, or owed an
 * allocation review, and it resolves nothing automatically: the workflow is
 * technician Stop -> allocation review -> then cancel or delete.
 *
 * The browser sends a ticket, an optional reason and — only when the vehicle is
 * still in a bay — an explicit location. Everything else is derived server-side
 * under the ticket transition lock.
 */
export type TerminalLocationKind = 'PARKING_SPOT' | 'GENERAL_LOT';

export type TerminalOutcome =
  /** The visit is now Cancelled or Deleted. */
  | 'TERMINALIZED'
  /** A repeat after a lost response. Not a second terminalization. */
  | 'ALREADY_TERMINAL'
  /** Someone is measuring right now. */
  | 'ACTIVE_WORK'
  /** Someone stopped mid-job intending to come back. */
  | 'PAUSED_WORK'
  /** Measured time exists that nobody has allocated to jobs yet. */
  | 'REVIEW_REQUIRED'
  /** Evidence that cannot be classified as working or finished. */
  | 'TIMER_INTEGRITY_BLOCK'
  /** The vehicle is in a bay; a human must say where it goes. */
  | 'LOCATION_REQUIRED'
  /** That space was taken while the answer was being given. */
  | 'LOCATION_CONFLICT'
  /** Already completed — correcting that is supersession, not deletion. */
  | 'TICKET_NOT_TERMINALIZABLE'
  /** Unknown, another tenant's, or one this caller may not end — one answer. */
  | 'TICKET_NOT_AVAILABLE'
  /** Terminal in text, but technician evidence contradicts it. */
  | 'TERMINAL_EVIDENCE_CONFLICT'
  /** One day of a multi-day rollover: never ended on its own (20270110100000). */
  | 'ROLLOVER_CHAIN_MEMBER'
/** No server answer. Does NOT mean nothing committed. */
  | 'SUBMIT_FAILED';

export interface TerminalResult {
  outcome: TerminalOutcome;
  ticketId: string;
  activeSessions: number;
  unresolvedPauses: number;
  awaitingReview: number;
  note: string | null;
}

const KNOWN: TerminalOutcome[] = [
  'TERMINALIZED', 'ALREADY_TERMINAL', 'ACTIVE_WORK', 'PAUSED_WORK',
  'REVIEW_REQUIRED', 'TIMER_INTEGRITY_BLOCK', 'LOCATION_REQUIRED',
  'LOCATION_CONFLICT', 'TICKET_NOT_TERMINALIZABLE', 'TICKET_NOT_AVAILABLE',
  'TERMINAL_EVIDENCE_CONFLICT', 'ROLLOVER_CHAIN_MEMBER',
];

function shape(data: unknown, ticketId: string): TerminalResult {
  const r = (data ?? {}) as Record<string, unknown>;
  // Unknown outcomes FAIL CLOSED. The vocabulary belongs to the server, and a
  // client too old to understand a new blocker must not report it as success.
  const outcome = KNOWN.includes(r.outcome as TerminalOutcome)
    ? (r.outcome as TerminalOutcome)
    : 'SUBMIT_FAILED';
  return {
    outcome,
    ticketId: (r.ticket_id as string) ?? ticketId,
    activeSessions: Number(r.active_sessions ?? 0),
    unresolvedPauses: Number(r.unresolved_pauses ?? 0),
    awaitingReview: Number(r.awaiting_review ?? 0),
    note: (r.note as string) ?? (r.reason as string) ?? null,
  };
}

async function call(
  fn: 'delete_ticket_if_quiescent' | 'cancel_ticket_if_quiescent',
  ticketId: string,
  reason: string | null,
  locationKind: TerminalLocationKind | null,
  parkingSpotId: string | null,
): Promise<TerminalResult> {
  const { data, error } = await (supabase as any).rpc(fn, {
    p_ticket_id: ticketId,
    p_reason: reason,
    p_location_kind: locationKind,
    p_parking_spot_id: locationKind === 'PARKING_SPOT' ? parkingSpotId : null,
  });
  if (error) {
    console.warn(`[${fn}] failed`, error);
    return { outcome: 'SUBMIT_FAILED', ticketId, activeSessions: 0,
             unresolvedPauses: 0, awaitingReview: 0, note: error.message };
  }
  return shape(data, ticketId);
}

export const deleteTicketIfQuiescent = (
  ticketId: string, reason?: string | null,
  locationKind: TerminalLocationKind | null = null,
  parkingSpotId: string | null = null,
) => call('delete_ticket_if_quiescent', ticketId, reason ?? null, locationKind, parkingSpotId);

export const cancelTicketIfQuiescent = (
  ticketId: string, reason?: string | null,
  locationKind: TerminalLocationKind | null = null,
  parkingSpotId: string | null = null,
) => call('cancel_ticket_if_quiescent', ticketId, reason ?? null, locationKind, parkingSpotId);

/** A repeat after a lost response is a success, not an error. */
export const isTerminalSettled = (o: TerminalOutcome) =>
  o === 'TERMINALIZED' || o === 'ALREADY_TERMINAL';

/**
 * Did THIS call perform the act, or merely find it already done?
 *
 * Both are successful outcomes for STATE, and the difference matters for
 * everything that is not state. A retry after a lost response must not emit a
 * second OCIS event, a second Pensieve record or a second cascade: the
 * transport was retried, the human did not delete the ticket twice.
 *
 * `isTerminalSettled` answers "may the UI move on?"; this answers "did a user
 * act just happen?". Side effects belong to the second question.
 *
 * Terminal-event source consolidation is 8G.3; this is only the minimum that
 * stops a retry from manufacturing history.
 */
export const didTerminalize = (o: TerminalOutcome) => o === 'TERMINALIZED';

/** Operator-readable explanation. Never says the work was ended. */
export function describeTerminalRefusal(r: TerminalResult): string {
  switch (r.outcome) {
    case 'ACTIVE_WORK':
      return r.activeSessions === 1
        ? 'A technician is still working on this ticket. They need to stop first.'
        : `${r.activeSessions} technicians are still working on this ticket. They need to stop first.`;
    case 'PAUSED_WORK':
      return r.unresolvedPauses === 1
        ? 'A technician has paused work here and intends to come back.'
        : `${r.unresolvedPauses} technicians have paused work here and intend to come back.`;
    case 'REVIEW_REQUIRED':
      return 'Measured time on this ticket still needs allocating to jobs.';
    case 'TIMER_INTEGRITY_BLOCK':
      return 'This ticket has timer evidence that needs a person to look at it.';
    case 'LOCATION_REQUIRED':
      return 'This vehicle is in a bay. Say where it goes before ending the visit.';
    case 'LOCATION_CONFLICT':
      return r.note ?? 'That space is not available.';
    case 'TICKET_NOT_TERMINALIZABLE':
      return 'This visit is already completed.';
    case 'TICKET_NOT_AVAILABLE':
      return 'This ticket is not available.';
    case 'TERMINAL_EVIDENCE_CONFLICT':
      return 'This ticket is already ended but still has live technician work on it. Someone needs to resolve that first.';
    case 'ROLLOVER_CHAIN_MEMBER':
      return 'This ticket is part of a multi-day rollover and cannot be deleted individually.';
    case 'SUBMIT_FAILED':
      return 'The result could not be confirmed. Try again.';
    default:
      return '';
  }
}
